import React, { useState } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import ExplanationBox from '../shared/ExplanationBox';
import GuardrailWarning from '../shared/GuardrailWarning';
import { Save } from 'lucide-react';

const Step0_Intake = () => {
  const { data, updateData, warnings, nextStep, currentDesignName, saveAsNewDesign, saveCurrentDesign, currentDesignId } = useWizard();
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [designName, setDesignName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  const aims = [
    { value: 'explore', label: 'Explore', description: 'Discover new insights, patterns, or generate ideas' },
    { value: 'explain', label: 'Explain', description: 'Understand why something happens or how things are related' },
    { value: 'evaluate', label: 'Evaluate', description: 'Assess effectiveness, outcomes, or value' },
    { value: 'improve', label: 'Improve', description: 'Develop solutions or enhance existing practice' },
    { value: 'predict', label: 'Predict', description: 'Forecast future outcomes based on patterns' }
  ];
  
  const dataTypes = [
    { value: 'numbers', label: 'Numbers & Statistics', description: 'Measurements, counts, scales, surveys' },
    { value: 'words', label: 'Words & Images', description: 'Interviews, observations, documents, videos' },
    { value: 'both', label: 'Both Numbers and Words', description: 'Combination of quantitative and qualitative data' },
    { value: 'not_sure', label: 'Not Sure Yet', description: 'I need guidance on what data to collect' }
  ];
  
  const questionTypes = [
    { value: 'difference', label: 'Differences Between Groups', description: 'Do groups differ? Is treatment A better than B?' },
    { value: 'relationship', label: 'Relationships Between Variables', description: 'Are X and Y related? Does X predict Y?' },
    { value: 'prediction', label: 'Prediction & Modeling', description: 'Can we predict Y from X? Build a model?' },
    { value: 'understanding_meaning', label: 'Understanding Meaning', description: 'What does this mean to participants? How do they experience it?' },
    { value: 'process_how_why', label: 'Process/How/Why', description: 'How does this process work? Why does this happen?' },
    { value: 'change_time', label: 'Change Over Time', description: 'How does it change? What are the trends?' }
  ];
  
  const settings = [
    { value: 'single_site', label: 'Single Organization/Site', description: 'One company, school, hospital, etc.' },
    { value: 'many_sites', label: 'Multiple Sites', description: 'Several organizations or locations' },
    { value: 'lab_controlled', label: 'Lab/Controlled', description: 'Controlled experimental setting' },
    { value: 'online', label: 'Online/Remote', description: 'Web-based, social media, remote participants' }
  ];
  
  const timeConstraints = [
    { value: 'one_time', label: 'One-Time', description: 'Collect data once (snapshot)' },
    { value: 'follow_over_time', label: 'Follow Over Time', description: 'Multiple data collection points (panel, cohort)' }
  ];
  
  const generalisability = [
    { value: 'high', label: 'High', description: 'Results should apply broadly to a population' },
    { value: 'medium', label: 'Medium', description: 'Some generalization within similar contexts' },
    { value: 'low', label: 'Low', description: 'In-depth understanding of specific case(s)' },
    { value: 'not_sure', label: 'Not Sure', description: 'Need guidance on this' }
  ];
  
  return (
    <div>
      <h2 className="step-title">Step 1: Fundamentals</h2>
      
      <ExplanationBox title="What is this?">
        <p>
          This is the starting point where we help you understand your research <strong>without technical jargon</strong>. 
          Answer the questions below in plain language, and we'll use your answers to recommend the best research methodology 
          (including philosophy, ontology, epistemology and axiology).
        </p>
        <p style={{ marginTop: '0.5rem' }}>
          Don't worry if you're unsure about some answers—we'll help you refine your choices later!
        </p>
      </ExplanationBox>
      
      {/* Question 1: Research Aims */}
      <div className="form-group">
        <label className="form-label">
          1. What are your fundamental research goals? <span className="label-hint">(Select up to 3)</span>
        </label>
        <div className="selection-counter">
          <span className={`counter-badge ${(data.intake.aims || []).length > 3 ? 'warning' : ''}`}>
            {(data.intake.aims || []).length} of 3 selected
          </span>
        </div>
        <div className="option-cards">
          {aims.map(aim => {
            const selectedAims = data.intake.aims || [];
            const isSelected = selectedAims.includes(aim.value);
            const isMaxReached = selectedAims.length >= 3 && !isSelected;
            
            const toggleAim = () => {
              if (isSelected) {
                updateData('intake', { 
                  ...data.intake, 
                  aims: selectedAims.filter(a => a !== aim.value) 
                });
              } else if (!isMaxReached) {
                updateData('intake', { 
                  ...data.intake, 
                  aims: [...selectedAims, aim.value] 
                });
              }
            };
            
            return (
              <div 
                key={aim.value}
                className={`option-card ${isSelected ? 'selected' : ''} ${isMaxReached ? 'disabled' : ''}`}
                onClick={toggleAim}
              >
                <div className="option-header">
                  <input 
                    type="checkbox"
                    name="aims"
                    value={aim.value}
                    checked={isSelected}
                    disabled={isMaxReached}
                    onChange={toggleAim}
                  />
                  <span className="option-title"><strong>{aim.label}</strong>: {aim.description}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Question 2: Data Type */}
      <div className="form-group">
        <label className="form-label">
          2. What kind of data will you collect?
        </label>
        <div className="option-cards">
          {dataTypes.map(type => (
            <div 
              key={type.value}
              className={`option-card ${data.intake.dataType === type.value ? 'selected' : ''}`}
              onClick={() => updateData('intake', { ...data.intake, dataType: type.value })}
            >
              <div className="option-header">
                <input 
                  type="radio"
                  name="dataType"
                  value={type.value}
                  checked={data.intake.dataType === type.value}
                  onChange={(e) => updateData('intake', { ...data.intake, dataType: e.target.value })}
                />
                <span className="option-title"><strong>{type.label}</strong>: {type.description}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Question 3: Question Types (multiple selection) */}
      <div className="form-group">
        <label className="form-label">
          3. What type of research question are you asking? <span className="label-hint">(Select up to 3)</span>
        </label>
        <div className="selection-counter">
          <span className={`counter-badge ${(data.intake.questionTypes || []).length > 3 ? 'warning' : ''}`}>
            {(data.intake.questionTypes || []).length} of 3 selected
          </span>
        </div>
        <div className="option-cards">
          {questionTypes.map(type => {
            const selectedTypes = data.intake.questionTypes || [];
            const isSelected = selectedTypes.includes(type.value);
            const isMaxReached = selectedTypes.length >= 3 && !isSelected;
            
            const toggleQuestionType = () => {
              if (isSelected) {
                updateData('intake', { 
                  ...data.intake, 
                  questionTypes: selectedTypes.filter(t => t !== type.value) 
                });
              } else if (!isMaxReached) {
                updateData('intake', { 
                  ...data.intake, 
                  questionTypes: [...selectedTypes, type.value] 
                });
              }
            };
            
            return (
              <div 
                key={type.value}
                className={`option-card ${isSelected ? 'selected' : ''} ${isMaxReached ? 'disabled' : ''}`}
                onClick={toggleQuestionType}
              >
                <div className="option-header">
                  <input 
                    type="checkbox"
                    name="questionTypes"
                    value={type.value}
                    checked={isSelected}
                    disabled={isMaxReached}
                    onChange={toggleQuestionType}
                  />
                  <span className="option-title"><strong>{type.label}</strong>: {type.description}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Question 4: Setting */}
      <div className="form-group">
        <label className="form-label">
          4. What's your research setting?
        </label>
        <div className="option-cards">
          {settings.map(setting => (
            <div 
              key={setting.value}
              className={`option-card ${data.intake.setting === setting.value ? 'selected' : ''}`}
              onClick={() => updateData('intake', { ...data.intake, setting: setting.value })}
            >
              <div className="option-header">
                <input 
                  type="radio"
                  name="setting"
                  value={setting.value}
                  checked={data.intake.setting === setting.value}
                  onChange={(e) => updateData('intake', { ...data.intake, setting: e.target.value })}
                />
                <span className="option-title"><strong>{setting.label}</strong>: {setting.description}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Question 5: Time Constraints */}
      <div className="form-group">
        <label className="form-label">
          5. Time constraints?
        </label>
        <div className="option-cards">
          {timeConstraints.map(time => (
            <div 
              key={time.value}
              className={`option-card ${data.intake.timeConstraints === time.value ? 'selected' : ''}`}
              onClick={() => updateData('intake', { ...data.intake, timeConstraints: time.value })}
            >
              <div className="option-header">
                <input 
                  type="radio"
                  name="timeConstraints"
                  value={time.value}
                  checked={data.intake.timeConstraints === time.value}
                  onChange={(e) => updateData('intake', { ...data.intake, timeConstraints: e.target.value })}
                />
                <span className="option-title"><strong>{time.label}</strong>: {time.description}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Question 6: Generalisability */}
      <div className="form-group">
        <label className="form-label">
          6. How generalizable should your results be?
        </label>
        <div className="option-cards">
          {generalisability.map(gen => (
            <div 
              key={gen.value}
              className={`option-card ${data.intake.generalisability === gen.value ? 'selected' : ''}`}
              onClick={() => updateData('intake', { ...data.intake, generalisability: gen.value })}
            >
              <div className="option-header">
                <input 
                  type="radio"
                  name="generalisability"
                  value={gen.value}
                  checked={data.intake.generalisability === gen.value}
                  onChange={(e) => updateData('intake', { ...data.intake, generalisability: e.target.value })}
                />
                <span className="option-title"><strong>{gen.label}</strong>: {gen.description}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {warnings.length > 0 && warnings.map((warning, idx) => (
        <GuardrailWarning key={idx} type={warning.type} title={warning.type === 'error' ? 'Required' : 'Note'}>
          {warning.message}
        </GuardrailWarning>
      ))}
      
      <NavigationButtons showPrevious={false} onNext={() => setShowSavePrompt(true)} />

      {showSavePrompt && (
        <div className="modal-overlay">
          <div className="save-prompt-modal">
            <div className="save-prompt-header">
              <Save size={32} className="save-prompt-icon" />
              <h3>Save Your Research Design</h3>
            </div>
            
            <p className="save-prompt-desc">
              Before continuing, please save your work with a unique name. 
              This ensures your progress is preserved and helps you identify this research design later.
            </p>
            
            <div className="save-prompt-input-group">
              <label>Design Name</label>
              <input
                type="text"
                className="save-prompt-input"
                placeholder="e.g., Customer Satisfaction Study v1"
                value={designName}
                onChange={(e) => setDesignName(e.target.value)}
                autoFocus
              />
              <span className="save-prompt-hint">
                Tip: Include your topic and version number for easy identification
              </span>
            </div>
            
            <div className="save-prompt-actions">
              <button 
                className="btn btn-primary"
                onClick={async () => {
                  if (designName.trim()) {
                    setIsSaving(true);
                    await saveAsNewDesign(designName.trim());
                    setIsSaving(false);
                    setShowSavePrompt(false);
                    nextStep();
                  }
                }}
                disabled={!designName.trim() || isSaving}
              >
                <Save size={16} />
                {isSaving ? 'Saving...' : 'Save & Continue'}
              </button>
            </div>
            
            {currentDesignId && (
              <div className="save-prompt-existing">
                <span>Current design: <strong>{currentDesignName}</strong></span>
                <button 
                  className="btn-link"
                  onClick={async () => {
                    setIsSaving(true);
                    await saveCurrentDesign();
                    setIsSaving(false);
                    setShowSavePrompt(false);
                    nextStep();
                  }}
                  disabled={isSaving}
                >
                  Update existing design instead
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Step0_Intake;
